import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export type FeedbackSurface = 'agent_message' | 'voice_call' | 'control_centre';
export type FeedbackRating = 'up' | 'down';

const cache = new Map<string, Map<string, FeedbackRating>>();
const loading = new Map<string, Promise<void>>();
const listeners = new Map<string, Set<() => void>>();

function key(surface: FeedbackSurface, targetId: string) {
  return `${surface}:${targetId}`;
}

function notify(businessId: string) {
  listeners.get(businessId)?.forEach((fn) => fn());
}

async function load(businessId: string): Promise<void> {
  const existing = loading.get(businessId);
  if (existing) return existing;
  const job = (async () => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return;
    const { data } = await supabase
      .from('ai_response_feedback')
      .select('surface, target_id, rating')
      .eq('business_id', businessId)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(2000);
    const map = cache.get(businessId) || new Map<string, FeedbackRating>();
    for (const row of data || []) {
      if (row.rating === 'up' || row.rating === 'down') {
        map.set(key(row.surface as FeedbackSurface, row.target_id), row.rating);
      }
    }
    cache.set(businessId, map);
    notify(businessId);
  })();
  loading.set(businessId, job);
  try {
    await job;
  } finally {
    loading.delete(businessId);
  }
}

export function useResponseRating(businessId: string | undefined, surface: FeedbackSurface, targetId: string) {
  const [rating, setRating] = useState<FeedbackRating | null>(null);

  useEffect(() => {
    if (!businessId || !targetId) return;
    const read = () => setRating(cache.get(businessId)?.get(key(surface, targetId)) || null);
    read();
    const set = listeners.get(businessId) || new Set<() => void>();
    set.add(read);
    listeners.set(businessId, set);
    void load(businessId);
    return () => { set.delete(read); };
  }, [businessId, surface, targetId]);

  const choose = async (next: FeedbackRating, excerpt: string) => {
    if (!businessId || !targetId) return;
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return;
    const map = cache.get(businessId) || new Map<string, FeedbackRating>();
    cache.set(businessId, map);
    const current = map.get(key(surface, targetId)) || null;
    const value = current === next ? null : next;
    if (value) map.set(key(surface, targetId), value);
    else map.delete(key(surface, targetId));
    notify(businessId);
    if (!value) {
      const { error } = await supabase
        .from('ai_response_feedback')
        .delete()
        .eq('business_id', businessId)
        .eq('user_id', userId)
        .eq('surface', surface)
        .eq('target_id', targetId);
      if (error) {
        if (current) map.set(key(surface, targetId), current);
        else map.delete(key(surface, targetId));
        notify(businessId);
      }
      return;
    }
    const { error } = await supabase.from('ai_response_feedback').upsert({
      business_id: businessId,
      user_id: userId,
      surface,
      target_id: targetId,
      rating: value,
      excerpt: excerpt.replace(/\s+/g, ' ').trim().slice(0, 2000),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,surface,target_id' });
    if (error) {
      if (current) map.set(key(surface, targetId), current);
      else map.delete(key(surface, targetId));
      notify(businessId);
    }
  };

  return { rating, choose };
}
